import { api } from "../base";
import type {
  IOptionStrategyCancelResponse,
  IOptionStrategyEntriesQuery,
  IOptionStrategyEntriesResponse,
} from "@pulse-ui/codegen-v2/generated_ts/option_strategy";
import type {
  IPriceLegsRequest,
  IPriceLegsResponse,
} from "@pulse-ui/codegen-v2/generated_ts/option_pricing";
import type {
  ITalosMultiDealResponse,
  ITalosStrategyDealRequest,
} from "@pulse-ui/codegen-v2/generated_ts/venues/talos";

export {
  OptionStrategyLegCancelOutcome,
  OptionStrategyLegLiveStatus,
  OptionStrategyLegResolutionState,
  type IOptionStrategyCancelPathParams,
  type IOptionStrategyCancelResponse,
  type IOptionStrategyEntriesQuery,
  type IOptionStrategyEntriesResponse,
  type IOptionStrategyEntry,
  type IOptionStrategyEntryLeg,
  type IOptionStrategyEntryView,
  type IOptionStrategyLegCancelResult,
  type IOptionStrategyLegResolution,
} from "@pulse-ui/codegen-v2/generated_ts/option_strategy";
export type {
  IPriceLegRequest,
  IPriceLegsRequest,
  IPriceLegsResponse,
  IPricedLeg,
} from "@pulse-ui/codegen-v2/generated_ts/option_pricing";
export type {
  ITalosStrategyDealRequest,
  ITalosStrategyLeg,
} from "@pulse-ui/codegen-v2/generated_ts/venues/talos";
export {
  OptionStrategyCategory,
  OptionStrategyType,
} from "@pulse-ui/codegen-v2/generated_ts/types";

const OPTION_STRATEGIES_URL = "/api/trading-ops/v1/option-strategies";

// fetchBaseQuery would send a null param as the literal string "null", so absent and null are dropped.
function presentParams(query: Partial<IOptionStrategyEntriesQuery> | void) {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined) params[key] = value;
  }
  return params;
}

const optionStrategiesEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Books a multi-leg option strategy; a 502 carries the same per-leg shape with each leg's failure. */
    bookOptionStrategy: build.mutation<ITalosMultiDealResponse, ITalosStrategyDealRequest>({
      query: (body) => ({ url: OPTION_STRATEGIES_URL, method: "POST", body }),
      invalidatesTags: ["OptionStrategies"],
    }),

    /** Booked strategy sets, newest first, one page per call. */
    getOptionStrategies: build.query<
      IOptionStrategyEntriesResponse,
      Partial<IOptionStrategyEntriesQuery> | void
    >({
      query: (query) => ({ url: OPTION_STRATEGIES_URL, params: presentParams(query) }),
      providesTags: ["OptionStrategies"],
    }),

    /** Cancels every leg of one strategy set; every leg's outcome is reported. */
    cancelOptionStrategy: build.mutation<IOptionStrategyCancelResponse, string>({
      query: (strategyEntryId) => ({
        url: `${OPTION_STRATEGIES_URL}/${strategyEntryId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["OptionStrategies"],
    }),

    /** Prices option legs via Haruko; a per-leg failure is an entry with `error`, never a request error. */
    priceOptionLegs: build.mutation<IPriceLegsResponse, IPriceLegsRequest>({
      query: (body) => ({
        url: "/api/option-pricer/v1/price-legs",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useBookOptionStrategyMutation,
  useGetOptionStrategiesQuery,
  useLazyGetOptionStrategiesQuery,
  useCancelOptionStrategyMutation,
  usePriceOptionLegsMutation,
} = optionStrategiesEndpoints;
